<template>
  <CCard>
    <CCardHeader class="d-flex justify-content-end">
      <h2 class="text-uppercase font-weight-bold mb-0">List of Classes</h2>
      <CButton
        v-if="$auth.check('classes_crud')"
        class="ml-auto mr-3"
        color="primary"
        @click="createClass"
      >
        Add New
      </CButton>

      <CButton class="mr-3" color="primary" @click="openFilters">Filters</CButton>
      <CInput
        v-model="filters.search"
        class="mb-0"
        placeholder="Search"
        @update:value="fetchData"
      />
    </CCardHeader>
    <Transition>
      <CCardHeader v-if="activeFiltersList.length > 0" class="active-filters-options">
        <div v-for="filter in activeFiltersList" :key="filter.title">
          <strong class="mr-2">{{ filter.title }}:</strong>
          <span
            v-for="option in filter.options"
            :key="`${filter.slug}${option.title}`"
            class="badge bg-info text-white mr-2 h6 c-pointer"
            @click="removeActiveFilter(filter.slug, option.value)"
          >
            {{ option.title }}
            <i class="fas fa-times" />
          </span>
        </div>
      </CCardHeader>
    </Transition>

    <CCardBody>
      <CDataTable
        :items="data"
        :fields="c_fields"
        :pagination="false"
        :sorter="{ external: true }"
        :loading="loading"
        class="mb-0"
        @update:sorter-value="sortingChange"
      >
        <template #name="data">
          <td>
            <span
              v-c-tooltip="{
                content: statuses[data.item.status],
                placement: 'left',
                appendToBody: true,
              }"
            >
              <span
                class="status-badge mr-1"
                :class="{
                  'bg-success': data.item.status === 'released',
                  'bg-secondary': data.item.status === 'draft',
                  'bg-dark': data.item.status === 'ready',
                  'bg-info': data.item.status === 'filmed',
                }"
              />
              {{ data.item.name }}
            </span>
          </td>
        </template>
        <template #categories="data">
          <td>
            <span v-for="cat in data.item.categories" class="badge bg-info text-white mr-1">
              {{ cat }}
            </span>
          </td>
        </template>
        <template #instructors="data">
          <td>
            {{ data.item.instructors.map((i) => i.name).join(', ') }}
          </td>
        </template>
        <template #release_date="data">
          <td>
            {{ $formatDateTime(data.item.release_date) }}
          </td>
        </template>
        <template #production_date="data">
          <td>
            {{ $formatDateTime(data.item.production_date) }}
          </td>
        </template>
        <template #actions="data">
          <td>
            <div class="d-flex justify-content-end">
              <CButton
                v-if="!data.item.release_date"
                size="sm"
                color="success"
                class="text-white mr-2"
                @click="releaseClass(data.item)"
              >
                Publish
              </CButton>
              <CButton
                v-else
                size="sm"
                color="dark"
                class="text-white mr-2"
                @click="unreleaseClass(data.item.id)"
              >
                Unpublish
              </CButton>
              <CButton
                size="sm"
                color="warning"
                class="text-white mr-2"
                @click="editClass(data.item.id)"
              >
                Edit
              </CButton>
              <CButton
                size="sm"
                color="danger"
                class="text-white"
                @click="deleteClass(data.item.id)"
              >
                Delete
              </CButton>
            </div>
          </td>
        </template>
      </CDataTable>
      <pagination
        :current-page="options.page"
        :last-page="options.last_page"
        class="mr-3"
        :loading="loading"
        @paginate="pageChange"
      />
    </CCardBody>
    <filters-modal ref="filters_modal" @apply="changeFilters" />
    <delete-confirm-modal ref="delete_modal" @confirmed="confirmDelete" />
    <delete-confirm-modal ref="unpublish_modal" @confirmed="confirmUnpublish" />
    <release-modal ref="release_modal" @confirmed="confirmRelease" />
  </CCard>
</template>

<script>
import externalTable from '@/mixins/externalTable';
import FiltersModal from '@/views/sessions/FiltersModal';
import DeleteConfirmModal from '@/views/sessions/DeleteConfirmModal';
import ReleaseModal from '@/views/sessions/ReleaseModal';
import { useAppStore } from '@/store';

export default {
  name: 'Sessions',
  components: { ReleaseModal, DeleteConfirmModal, FiltersModal },
  mixins: [externalTable],
  setup() {
    const appStore = useAppStore();
    return { appStore };
  },
  data() {
    return {
      filters: {
        search: '',
        instructor: [],
        class_type: [],
        language: [],
        tag: [],
        category: [],
        status: null,
      },
      statuses: {
        released: 'Released',
        draft: 'Draft',
        ready: 'Ready',
        filmed: 'Filmed',
      },
      fields: [
        {
          key: 'name',
          label: 'Name',
        },
        {
          key: 'categories',
          label: 'Categories',
          sorter: false,
        },
        {
          key: 'instructors',
          label: 'Instructors',
          sorter: false,
        },
        {
          key: 'length',
          label: 'Length',
        },
        {
          key: 'class_type',
          label: 'Type',
        },
        {
          key: 'language',
          label: 'Language',
        },
        {
          key: 'production_date',
          label: 'Production Date',
        },
        {
          key: 'release_date',
          label: 'Release Date',
        },
      ],
      url: '/sessions',
    };
  },
  computed: {
    filtersData() {
      return this.appStore.classesFilterData;
    },
    c_fields() {
      let fields = this.fields.slice(0);
      // if(this.$auth.check('sessions_crud')){
      if (true) {
        fields.push({
          key: 'actions',
          label: 'Actions',
          sorter: false,
        });
      }
      return fields;
    },
    activeFiltersList() {
      let filters = [];
      let getFilterOptionNameById = (optionName, id) => {
        let option = this.filtersData[optionName].find((o) => o.id === id);
        return option ? option.name : '';
      };

      //prepare status
      if (this.filters.status) {
        filters.push({
          title: 'Status',
          slug: 'status',
          options: [{ title: this.statuses[this.filters.status], value: this.filters.status }],
        });
      }
      //prepare other options
      [
        { title: 'Instructors', slug: 'instructor', filterField: 'instructors' },
        { title: 'Languages', slug: 'language', filterField: 'languages' },
        { title: 'Class types', slug: 'class_type', filterField: 'class_types' },
        { title: 'Categories', slug: 'category', filterField: 'categories' },
        { title: 'Tags', slug: 'tag', filterField: 'tags' },
      ].forEach(({ title, slug, filterField }) => {
        if (this.filters[slug].length) {
          filters.push({
            title,
            slug,
            options: this.filters[slug].map((valueId) => ({
              value: valueId,
              title: getFilterOptionNameById(filterField, valueId),
            })),
          });
        }
      });

      return filters;
    },
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    openFilters() {
      this.$refs.filters_modal.open(this.filters);
    },
    changeFilters(data) {
      this.options.page = 1;
      this.filters = { ...this.filters, ...data };
      this.fetchData();
    },
    createClass() {
      this.$router.push({ name: 'New Class' });
    },
    editClass(id) {
      this.$router.push({ name: 'Edit Class', params: { id: id } });
    },
    unreleaseClass(id) {
      this.$refs.unpublish_modal.open(id);
    },
    releaseClass(item) {
      this.$refs.release_modal.open(item);
    },
    deleteClass(id) {
      this.$refs.delete_modal.open(id);
    },
    confirmUnpublish(id) {
      this.$http
        .put('/sessions/' + id + '/unrelease')
        .then((data) => {
          this.$toast.success('Unpublished');
          this.fetchData();
        })
        .catch(({ response }) => {
          this.$toast.error(response.data.message);
          // this.setErrors(response.data.errors);
        });
    },
    confirmDelete(id) {
      this.$http.delete('/sessions/' + id + '').then(() => {
        this.$toast.success('Deleted!');
        this.fetchData();
      });
    },
    confirmRelease() {
      this.fetchData();
    },
    removeActiveFilter(filterFieldName, optionValue) {
      this.filters[filterFieldName] = Array.isArray(this.filters[filterFieldName])
        ? this.filters[filterFieldName].filter((value) => value !== optionValue)
        : null;
      this.fetchData();
    },
  },
};
</script>

<style scoped lang="scss">
.active-filters-options {
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
  & > div {
    line-height: 2.2rem;
    margin-right: 0.7rem;
  }
}
</style>
